import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Row,
} from 'reactstrap';
import { useHistory } from 'react-router-dom';

import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { clickImg } from '../../../utils';
import coupangProductService from './coupangProduct.service';

function CoupangProductForm({ match }) {
  const history = useHistory();
  const { id } = match.params;

  const [loading, setLoading] = useState(false);
  const [inputs, setInputs] = useState({
    name: '',
    url: '',
    active: true,
  });

  const [file, setFile] = useState('');
  const [previewURL, setPreviewURL] = useState('');
  const fileRef = useRef();

  const onChange = (e) => {
    const { name, value } = e.target;
    setInputs({
      ...inputs,
      [name]: value,
    });
  };

  const fetchData = async () => {
    if (!id) {
      return;
    }

    setLoading(true);
    try {
      const res = await coupangProductService.detail(id);
      if (res.code === 200) {
        setInputs({
          name: res.data.name || '',
          url: res.data.url || '',
          active: !!res.data.active,
        });
        if (res.data.thumbnail) {
          setPreviewURL(res.data.thumbnail);
        }
      }
    } catch (e) {
      alert(e.response?.data?.resultMsg || '데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleFileOnChange = (event) => {
    event.preventDefault();
    const selectedFile = event.target.files[0];
    const reader = new FileReader();

    reader.onloadend = () => {
      setFile(selectedFile);
      setPreviewURL(reader.result);
    };

    if (selectedFile) {
      reader.readAsDataURL(selectedFile);
    }
  };

  const submit = async () => {
    if (!inputs.name.trim()) {
      alert('상품명을 입력해주세요.');
      return;
    }

    if (!inputs.url.trim()) {
      alert('URL을 입력해주세요.');
      return;
    }

    if (!file && !previewURL) {
      alert('썸네일 이미지를 등록해주세요.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: inputs.name,
        url: inputs.url,
        active: inputs.active,
      };

      let res;
      if (id) {
        res = await coupangProductService.update(id, payload);
      } else {
        res = await coupangProductService.register(payload);
      }

      if (res.code === 200) {
        const coupangProductId = res.data?.coupangProductId || id;
        if (file) {
          const formData = new FormData();
          formData.append('image', file);
          await coupangProductService.fileRegister(coupangProductId, formData);
        }
        alert(`${id ? '수정' : '등록'}되었습니다.`);
        history.push('/admin/coupangProductList');
      }
    } catch (e) {
      alert(e.response?.data?.resultMsg || '저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    if (!id) {
      return;
    }

    if (!window.confirm('삭제하시겠습니까?')) {
      return;
    }

    setLoading(true);
    try {
      const res = await coupangProductService.remove(id);
      if (res.code === 200) {
        alert('삭제되었습니다.');
        history.push('/admin/coupangProductList');
      }
    } catch (e) {
      alert(e.response?.data?.resultMsg || '삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SimpleHeader name="Form elements" parentName="Forms" />
      <Container className="mt--6" fluid>
        <Card className="mb-4">
          <CardHeader>
            <Row>
              <Col xs="11">
                <h3 className="mb-0">쿠팡 상품 {id ? '상세' : '등록'}</h3>
              </Col>
              <Col className="text-right" xs="12">
                <Button className="btn-round btn-icon" color="primary" onClick={submit} size="sm" disabled={loading}>
                  <span className="btn-inner--text">{id ? '저장하기' : '등록하기'}</span>
                </Button>
                {id && (
                  <Button className="btn-round btn-icon" color="danger" onClick={remove} size="sm" disabled={loading}>
                    <span className="btn-inner--text">삭제하기</span>
                  </Button>
                )}
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md="6">
                <FormGroup>
                  <label className="form-control-label">상품명 <span className="text-red">(필수)</span></label>
                  <Input
                    placeholder="상품명을 입력해주세요"
                    type="text"
                    name="name"
                    value={inputs.name}
                    onChange={onChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                <FormGroup>
                  <label className="form-control-label">URL <span className="text-red">(필수)</span></label>
                  <Input
                    placeholder="https://..."
                    type="text"
                    name="url"
                    value={inputs.url}
                    onChange={onChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                <FormGroup>
                  <label className="form-control-label">활성여부 <span className="text-red">(필수)</span></label>
                  <Input
                    type="select"
                    value={inputs.active ? 'true' : 'false'}
                    onChange={(e) => setInputs({ ...inputs, active: e.target.value === 'true' })}
                  >
                    <option value="true">활성</option>
                    <option value="false">비활성</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row className="mt-4">
              <Col xs="12">
                <label className="form-control-label">썸네일 이미지 1장 <span className="text-red">(필수)</span></label>
                <input
                  ref={fileRef}
                  hidden
                  type="file"
                  onChange={handleFileOnChange}
                />
                <Button
                  className="ml-1"
                  color="primary"
                  size="md"
                  type="button"
                  onClick={() => fileRef.current.click()}
                >
                  첨부하기
                </Button>
                <div className="mt-3">
                  {previewURL && (
                    <img
                      style={{
                        height: '150px',
                        borderRadius: '5px',
                        marginRight: '20px',
                        cursor: 'pointer',
                      }}
                      src={previewURL}
                      onClick={() => clickImg(previewURL)}
                    />
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </>
  );
}

export default CoupangProductForm;
